import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calendar, Home, ReceiptText, Wallet, ClipboardCheck, Palette, Users, LogOut, Menu, ShieldCheck, FileClock, UserRound, Building2, FileText, ListChecks, Trash2, Zap, Sparkles, AlertTriangle, Clock,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import { getUser, clearSession } from "../lib/auth";
import api from "../lib/api";

const NAV_ITEMS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, roles: ["diretor", "gestor", "financeiro", "operacional"] },
  { to: "/admin/reservas", label: "Reservas", icon: ReceiptText, roles: ["diretor", "gestor", "financeiro", "operacional"] },
  { to: "/admin/contratos", label: "Contratos", icon: FileText, roles: ["diretor", "gestor"] },
  { to: "/admin/checkin-checkout", label: "Check-in / Check-out", icon: ListChecks, roles: ["diretor", "gestor", "operacional"] },
  { to: "/admin/hospedes", label: "Hóspedes", icon: UserRound, roles: ["diretor", "gestor", "financeiro"] },
  { to: "/admin/calendario", label: "Calendário", icon: Calendar, roles: ["diretor", "gestor", "financeiro", "operacional"] },
  { to: "/admin/casas", label: "Casas", icon: Home, roles: ["diretor", "gestor"] },
  { to: "/admin/pagamentos", label: "Pagamentos", icon: Wallet, roles: ["diretor", "gestor", "financeiro"] },
  { to: "/admin/tarefas", label: "Tarefas", icon: ClipboardCheck, roles: ["diretor", "gestor", "operacional"] },
  { to: "/admin/marca", label: "Marca", icon: Palette, roles: ["diretor", "gestor"] },
  { to: "/admin/institucional", label: "Institucional", icon: Building2, roles: ["diretor", "gestor", "financeiro", "operacional"] },
  { to: "/admin/precificacao", label: "Precificação Sazonal", icon: Sparkles, roles: ["diretor", "gestor"] },
  { to: "/admin/integracoes/pricelabs", label: "PriceLabs", icon: Zap, roles: ["diretor", "gestor", "financeiro"] },
  { to: "/admin/usuarios", label: "Usuários", icon: Users, roles: ["diretor"] },
  { to: "/admin/auditoria", label: "Auditoria", icon: FileClock, roles: ["diretor", "financeiro"] },
  { to: "/admin/lixeira", label: "Lixeira", icon: Trash2, roles: ["diretor", "gestor"] },
  { to: "/admin/seguranca", label: "Segurança (MFA)", icon: ShieldCheck, roles: ["diretor", "gestor", "financeiro", "operacional"] },
];

const ROLE_LABEL = {
  diretor: "Diretor",
  gestor: "Gestor de reservas",
  financeiro: "Financeiro",
  operacional: "Operacional",
};

export default function AdminShell() {
  const user = getUser();
  const nav = useNavigate();

  function logout() {
    clearSession();
    nav("/admin/login");
  }

  if (!user) {
    return <div />;
  }

  const items = NAV_ITEMS.filter((i) => i.roles.includes(user.role));

  const Sidebar = () => (
    <div className="w-64 bg-[#22302E] text-white/70 min-h-screen flex flex-col" data-testid="admin-sidebar">
      <div className="p-6 border-b border-white/10">
        <div className="font-display text-2xl text-white tracking-tight">Azuhomes</div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#3B9FD4] mt-1">Painel administrativo</div>
      </div>
      <nav className="flex-1 py-4">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/admin"}
            data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                isActive ? "border-[#3B9FD4] bg-white/5 text-white" : "border-transparent hover:text-white"
              }`
            }
          >
            <it.icon className="w-4 h-4" />
            {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-6 border-t border-white/10">
        <div className="text-sm text-white">{user.name}</div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400 mt-0.5">
          {ROLE_LABEL[user.role]}
        </div>
        <button
          data-testid="admin-logout"
          onClick={logout}
          className="mt-4 text-xs text-slate-400 hover:text-white flex items-center gap-2"
        >
          <LogOut className="w-3.5 h-3.5" />
          Sair
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-admin">
      <div className="flex">
        <div className="hidden md:block">
          <Sidebar />
        </div>

        <div className="md:hidden fixed top-3 left-3 z-40">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon"><Menu className="w-4 h-4" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 bg-[#22302E] w-64">
              <Sidebar />
            </SheetContent>
          </Sheet>
        </div>

        <main className="flex-1 min-w-0">
          <ICalBanners />
          <div className="p-6 md:p-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function ICalBanners() {
  const [health, setHealth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await api.get("/admin/ical/health");
        if (!cancelled) setHealth(r.data);
      } catch { /* silent — banner só aparece se conseguir consultar */ }
    }
    load();
    // Recarrega a saúde a cada 5 minutos enquanto o admin está aberto.
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!health || health.total_feeds === 0) return null;

  return (
    <div className="border-b border-slate-200">
      {health.unhealthy_count > 0 && (
        <div
          className="bg-rose-50 border-b border-rose-200 px-6 py-2.5 flex items-start gap-2 text-sm text-rose-900"
          data-testid="ical-banner-unhealthy"
          role="alert"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>{health.unhealthy_count} calendário{health.unhealthy_count > 1 ? "s externos" : " externo"} com falha</strong>{" "}
            de sincronização — última atualização há mais de {health.stale_threshold_hours}h ou com erro.
            <NavLink to="/admin/casas" className="underline ml-1 hover:text-rose-950">Ver casas afetadas</NavLink> e conferir manualmente
            reservas do período enquanto o feed não voltar.
          </div>
        </div>
      )}
      <div
        className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-start gap-2 text-xs text-amber-900"
        data-testid="ical-banner-delay-notice"
      >
        <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Sincronização de calendário externo tem atraso de até algumas horas —
          confira manualmente reservas recentes antes de confirmar disponibilidade em casos limítrofes.
        </span>
      </div>
    </div>
  );
}
